import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { SPINNER_ICON, STAR_ICON, STAR_ICON_FILL } from 'app/constants';
import { head, eq, find } from 'lodash';
import { AuthorProfile, Notification } from 'app/models';
import {
  addAuthorToProject,
  deleteAuthorToProject,
  updateAuthorLabel,
  initAuthorOutletToProjectState,
  resetAuthorProfile,
  resetSearch,
  showNotification,
  tabControl,
  unSetSearch,
} from 'app/redux/actions';
import { AuthenticationService, AuthorService } from 'app/services';
import { AuthService } from '@auth0/auth0-angular';

@Component({
  selector: 'app-author-contact-profile',
  templateUrl: './author-contact-profile.component.html',
})
export class AuthorOutletProfileComponent implements OnInit {
  public actionType: string;
  public isAuthorSaved: boolean;
  public project: any;
  public starToggle: string;
  public author: AuthorProfile;
  public authorsOutletsList: any[];
  public picture: string;
  public isAddedingOrIsRemoving: boolean;
  public showAuthorProfile: boolean;
  public savedAuthor: any;
  public categories: any[];
  public readonly shortcutLabels = ['Primary', 'Secondary'];

  constructor(
    private store: Store<AppState>,
    private authorService: AuthorService,
    private auth: AuthenticationService
  ) {
    this.author = null;
    this.isAuthorSaved = false;
    this.actionType = null;
    this.project = null;
    this.starToggle = STAR_ICON;
    this.authorsOutletsList = [];
    this.isAddedingOrIsRemoving = false;
    this.showAuthorProfile = true;
    this.savedAuthor = null;
    this.categories = [];
  }

  ngOnInit() {
    this.store.select('search').subscribe(({ searchBody }) => {
      this.showAuthorProfile = searchBody?.authors?.length < 2;
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.project = projectSelected;
      }
    });

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    });

    this.store
      .select('authorContactDetails')
      .subscribe(({ authorContactProfile }) => {
        this.author = null;
        if (authorContactProfile) {
          this.author = authorContactProfile;
          this.picture = this.authorService.getAuthorPicture(
            authorContactProfile.id
          );
          this.setAuthorSaved();
        }
      });

    this.store
      .select('authorsOutletsList')
      .subscribe(
        ({
          authors,
          isAddingAuthorOutlet,
          isDeletingAuthorOutlet,
          hasAuthorOutletDeleted,
          hasAuthorOutletAdded,
          errors,
        }) => {
          this.authorsOutletsList = authors;

          if (isAddingAuthorOutlet || isDeletingAuthorOutlet) {
            this.starToggle = SPINNER_ICON;
          }

          if (hasAuthorOutletAdded) {
            this.setAuthorSaved();
          }

          if (hasAuthorOutletDeleted) {
            this.setAuthorSaved();
          }

          if (errors?.message) {
            this.store.dispatch(
              showNotification({
                notify: new Notification(
                  `Error ${this.actionType} author to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setAuthorSaved();
            setTimeout(() => {
              this.store.dispatch(initAuthorOutletToProjectState());
            }, 500);
          }
        }
      );
  }

  handleSaveAuthor(author) {
    if (!this.isAddedingOrIsRemoving) {
      this.isAddedingOrIsRemoving = true;
      this.starToggle = SPINNER_ICON;
      if (this.isAuthorSaved) {
        this.actionType = 'deleting';
        this.store.dispatch(
          deleteAuthorToProject({
            partyId: author.id,
            userProject: this.project,
            author,
          })
        );
      } else {
        this.actionType = 'adding';
        this.store.dispatch(
          addAuthorToProject({
            partyId: author.id,
            userProject: this.project,
            author,
          })
        );
      }
    }
  }

  handleIgnoreAuthor(currentlyIgnored: boolean) {
    if (currentlyIgnored) {
      this.handleSaveAuthor(this.author);
      return;
    }
    const ignore = this.findCategory('Ignore');
    if (!ignore) {
      this.showLabelError('Ignore');
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(
      addAuthorToProject({
        partyId: this.author.id,
        userProject: this.project,
        author: this.author,
        userLabelId: ignore.id,
      })
    );
  }

  handleAuthorLabel(labelName: string) {
    const category = this.findCategory(labelName);
    if (!category || !this.savedAuthor) {
      this.showLabelError(labelName);
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(
      updateAuthorLabel({
        userPartyId: this.savedAuthor.id,
        userProject: this.project,
        author: this.author,
        userLabelId: this.authorLabel === labelName ? null : category.id,
      })
    );
  }

  get isAuthorIgnored(): boolean {
    return this.authorLabel === 'Ignore';
  }

  get authorLabel(): string {
    return (
      this.savedAuthor?.categoryName || this.savedAuthor?.label?.name || null
    );
  }

  handleBackToHome() {
    this.store.dispatch(resetAuthorProfile());
    this.store.dispatch(tabControl({ index: 0 }));
    this.store.dispatch(unSetSearch());
    this.store.dispatch(resetSearch({ onReset: true }));
  }

  private setAuthorSaved() {
    setTimeout(() => {
      this.savedAuthor = find(this.authorsOutletsList, (i) =>
        eq(i.partyId, this.author.id)
      );
      this.isAuthorSaved = !!this.savedAuthor;
      this.starToggle = this.isAuthorSaved ? STAR_ICON_FILL : STAR_ICON;
      this.isAddedingOrIsRemoving = false;
    }, 500);
  }

  private findCategory(name: string): any {
    return find(
      this.categories,
      (category) => String(category.name).toLowerCase() === name.toLowerCase()
    );
  }

  private showLabelError(labelName: string): void {
    this.store.dispatch(
      showNotification({
        notify: new Notification(
          `The ${labelName} label is not available`,
          'error',
          'tl'
        ),
      })
    );
  }

  hasReportingAccess() {
    return this.auth.hasReportingAccess();
  }
}
